/* global console, document, HTMLAnchorElement, Office */

import { traceActivityHandler } from '../activity-handler/trace-activity-handler';
import type { AddinOrganisation } from '../organisations/addin-organisation.model';
import { fetchGetManagedOrganisations } from '../organisations/fetch-get-managed-organisations';
import { addinOrganisationContext } from '../organisations/organisation-context.store';
import {
    getOperatingOrgCodeForTitle,
    resolveAddinOperatingOrgCode,
} from '../organisations/resolve-addin-operating-org-code.util';
import { writeTabularSheetToWorksheet } from '../shared/sheet-writer/write-tabular-sheet-to-worksheet.util';
import { saveTraceSheetFiltersNamedItemForSheet } from './utils/trace-sheet-filter-named-item.util';
import { traceTokenStore } from '../token/trace-token-store';
import { traceUserInfoStore } from '../user-info/trace-user-info-store';
import { TaskPaneOfficeAuthFlow, type SwitchOrganisationClickOutcome } from './auth-flow';
import { openOrganisationSelectorDialog } from './organisation-selector/organisation-selector-dialog';
import { syncTaskPaneChromeWithAuthState } from './chrome-sync';
import { ReportingTaskPaneFilters } from './reporting-filters';
import { taskPaneUiSession } from './session';
import {
    CONNECT_BUTTON_LABEL_SIGN_IN,
    CONTINUE_SWITCH_ORGANISATION_BUTTON_LABEL,
    SWITCH_ORGANISATION_BUTTON_LABEL,
    TRACE_ADDIN_PRIVACY_POLICY_URL,
    TRACE_ADDIN_TERMS_OF_USE_URL,
} from './taskpane.constants';
import { logAuthDebug } from './utils/auth-debug.util';
import {
    clearOrgCodeFieldValidation,
    restoreLastOrgCodeInputFromSession,
    setOrgCodeInputDisabled,
} from './utils/org-code-field.util';
import { confirmFetchIfActiveSheetMayBeOverwritten } from './utils/overwrite-dialog.util';
import { clearReportFilterFieldHighlights } from './utils/report-filter-field-validation-highlights.util';
import { setupTaskpaneActionHandlers } from './utils/setup-taskpane-action-handlers.util';
import { registerTraceSheetFilterWorksheetActivation } from './utils/register-trace-sheet-filter-worksheet-activation.util';

/**
 * Tokens are session-scoped: {@link traceTokenStore} uses `sessionStorage`, so they survive task-pane
 * reloads until the browsing context ends (exact desktop WebView lifecycle varies by host). Not
 * cross-device. Signing out clears them.
 */

const reportDataCard = new ReportingTaskPaneFilters({
    onSyncUi: () => syncTaskPaneChromeFromAuth(),
    tokens: traceTokenStore,
    userInfo: traceUserInfoStore,
    agreementsApi: traceActivityHandler,
    materialSummaryApi: traceActivityHandler,
    dwtPayloadsApi: traceActivityHandler,
    dwtDefraSubmissionsApi: traceActivityHandler,
    tabularSheetWriter: { writeTabularSheetToWorksheet },
    fetchConfirm: { confirmFetchIfActiveSheetMayBeOverwritten },
    sheetFilters: {
        saveAfterSuccessfulFetch: async ({ sheetId, snapshot }) => {
            try {
                await saveTraceSheetFiltersNamedItemForSheet(sheetId, snapshot);
            } catch (e) {
                console.warn('[trace-reports-excel-addin] Could not persist sheet filters', e);
            }
        },
    },
});

/** Set when worksheet filter activation is registered; re-run after login so restore sees a session. */
let scheduleTraceSheetFilterRestore: () => void = () => {};

const UNABLE_TO_LOAD_ORGANISATIONS = 'Unable to load your organisations. Please try again.';
const SELECT_ORGANISATION_REQUIRED = 'Please select an organisation to continue.';

let managedOrganisationsLoad: Promise<boolean> | null = null;
let organisationGate: Promise<boolean> | null = null;

const officeAuthFlow = new TaskPaneOfficeAuthFlow({
    syncUi: syncTaskPaneChromeFromAuth,
    afterSuccessfulLogin: () => {
        void enterSessionAfterAuthentication(true);
    },
    onSwitchOrganisationRequested: () => handleSwitchOrganisationRequested(),
});

// ---------------------------------------------------------------------------
// Excel task pane entry
// ---------------------------------------------------------------------------

Office.onReady((info) => {
    if (info.host !== Office.HostType.Excel) {
        return;
    }

    bootstrapTraceReportsTaskPane();
});

/** One-time startup when the add-in task pane loads in Excel. */
function bootstrapTraceReportsTaskPane(): void {
    wireLegalLinkHrefs();
    wireAddinVersionLabel();
    restoreLastOrgCodeInputFromSession();
    registerTaskPaneDomHandlers();
    syncTaskPaneChromeFromAuth();
    reportDataCard.handleReportingTargetChange();
    void (async () => {
        if (traceTokenStore.hasConnectedSession()) {
            await enterSessionAfterAuthentication(false);
        }
        scheduleTraceSheetFilterRestore = registerTraceSheetFilterWorksheetActivation({
            tokens: traceTokenStore,
            userInfo: traceUserInfoStore,
            reporting: reportDataCard,
        });
        if (
            traceTokenStore.hasConnectedSession() &&
            !taskPaneUiSession.isOrganisationResolutionPending()
        ) {
            scheduleTraceSheetFilterRestore();
        }
    })();
}

/**
 * After Keycloak sign-in, or when the task pane reloads with a session.
 * A single organisation connects immediately. Several organisations require a choice, as in Trace.
 */
async function enterSessionAfterAuthentication(freshLogin: boolean): Promise<void> {
    taskPaneUiSession.setSwitchOrganisationFlowActive(false);
    taskPaneUiSession.setOrganisationResolutionPending(true);
    taskPaneUiSession.setOrganisationResolutionError(null);
    syncTaskPaneChromeFromAuth();

    const ready = await startOrganisationGate(freshLogin);
    taskPaneUiSession.setOrganisationResolutionPending(!ready);
    syncTaskPaneChromeFromAuth();
    if (!ready) {
        return;
    }
    await reportDataCard.refreshUserInfoAndRenderPanels();
    scheduleTraceSheetFilterRestore();
}

function startOrganisationGate(freshLogin: boolean): Promise<boolean> {
    if (organisationGate) {
        return organisationGate;
    }
    organisationGate = resolveOrganisationGate(freshLogin).finally(() => {
        organisationGate = null;
    });
    return organisationGate;
}

async function resolveOrganisationGate(freshLogin: boolean): Promise<boolean> {
    if (freshLogin) {
        addinOrganisationContext.clearSessionSelection();
    }
    const loaded = await ensureManagedOrganisationsLoaded();
    if (!loaded) {
        if (!freshLogin && resolveAddinOperatingOrgCode()) {
            taskPaneUiSession.setOrganisationResolutionError(null);
            return true;
        }
        taskPaneUiSession.setOrganisationResolutionError(UNABLE_TO_LOAD_ORGANISATIONS);
        return false;
    }
    if (
        !addinOrganisationContext.canSwitchOrganisation() ||
        addinOrganisationContext.selectedOrganisation()
    ) {
        taskPaneUiSession.setOrganisationResolutionError(null);
        return true;
    }

    const selected = await openOrganisationSelectorDialog({
        organisations: addinOrganisationContext.availableOrganisations(),
        selectedCode: null,
        title: 'Select Organisation',
        confirmLabel: 'Switch',
        cancelLabel: '',
    });
    if (!selected) {
        taskPaneUiSession.setOrganisationResolutionError(SELECT_ORGANISATION_REQUIRED);
        return false;
    }
    addinOrganisationContext.selectOrganisation(selected);
    taskPaneUiSession.setOrganisationResolutionError(null);
    return true;
}

async function ensureManagedOrganisationsLoaded(): Promise<boolean> {
    if (addinOrganisationContext.isLoaded()) {
        return true;
    }
    if (!managedOrganisationsLoad) {
        managedOrganisationsLoad = loadManagedOrganisationsFromApi().finally(() => {
            managedOrganisationsLoad = null;
        });
    }
    return managedOrganisationsLoad;
}

async function loadManagedOrganisationsFromApi(): Promise<boolean> {
    if (traceTokenStore.shouldRefreshAccessToken()) {
        const refreshed = await traceTokenStore.refreshAccessToken();
        if (!refreshed) {
            return false;
        }
    }
    const accessToken = traceTokenStore.getTokenData()?.accessToken;
    if (!accessToken) {
        return false;
    }
    const result = await fetchGetManagedOrganisations(accessToken);
    if (result.kind === 'failure') {
        console.error('Get-ManagedOrganisations failed', result.message);
        return false;
    }
    addinOrganisationContext.setAvailableOrganisations(result.organisations);
    if (result.organisations.length === 1) {
        addinOrganisationContext.selectOrganisation(result.organisations[0]);
    }
    return true;
}

/** Switch organisation: multi-org list without signing in again, otherwise the org-code prompt. */
async function handleSwitchOrganisationRequested(): Promise<SwitchOrganisationClickOutcome> {
    if (organisationGate) {
        await organisationGate;
        return 'handled';
    }

    const loaded = await ensureManagedOrganisationsLoaded();
    if (!loaded) {
        taskPaneUiSession.setOrganisationResolutionError(UNABLE_TO_LOAD_ORGANISATIONS);
        if (!resolveAddinOperatingOrgCode()) {
            taskPaneUiSession.setOrganisationResolutionPending(true);
        }
        syncTaskPaneChromeFromAuth();
        return 'handled';
    }
    taskPaneUiSession.setOrganisationResolutionError(null);

    if (!addinOrganisationContext.canSwitchOrganisation()) {
        return 'reauthenticate';
    }

    const currentCode = addinOrganisationContext.selectedOrganisationCode();
    const requireSelection = addinOrganisationContext.selectedOrganisation() === null;
    const selected = await openOrganisationSelectorDialog({
        organisations: addinOrganisationContext.availableOrganisations(),
        selectedCode: currentCode || null,
        title: requireSelection ? 'Select Organisation' : 'Switch Organisation',
        confirmLabel: 'Switch',
        cancelLabel: requireSelection ? '' : 'Cancel',
    });
    if (!selected) {
        if (requireSelection) {
            taskPaneUiSession.setOrganisationResolutionError(SELECT_ORGANISATION_REQUIRED);
            taskPaneUiSession.setOrganisationResolutionPending(true);
            syncTaskPaneChromeFromAuth();
        }
        return 'handled';
    }
    if (!requireSelection && selected.organisationCode === currentCode) {
        return 'handled';
    }
    await applySelectedOrganisation(selected);
    return 'handled';
}

async function applySelectedOrganisation(organisation: AddinOrganisation): Promise<void> {
    addinOrganisationContext.selectOrganisation(organisation);
    traceUserInfoStore.clear();
    reportDataCard.resetReportingUi({ clearSummary: true });
    reportDataCard.resetTabularWriteSession();
    taskPaneUiSession.setOrganisationResolutionPending(false);
    taskPaneUiSession.setOrganisationResolutionError(null);
    taskPaneUiSession.setSwitchOrganisationFlowActive(false);
    syncTaskPaneChromeFromAuth();
    await reportDataCard.refreshUserInfoAndRenderPanels();
    scheduleTraceSheetFilterRestore();
}

function wireAddinVersionLabel(): void {
    const el = document.getElementById('trace-addin-version');
    if (el) {
        el.textContent = `Version ${__TRACE_ADDIN_MANIFEST_VERSION__}`;
    }
}

function wireLegalLinkHrefs(): void {
    const terms = document.getElementById('legal-link-terms') as HTMLAnchorElement | null;
    const privacy = document.getElementById('legal-link-privacy') as HTMLAnchorElement | null;
    if (terms) {
        terms.href = TRACE_ADDIN_TERMS_OF_USE_URL;
    }
    if (privacy) {
        privacy.href = TRACE_ADDIN_PRIVACY_POLICY_URL;
    }
}

function registerTaskPaneDomHandlers(): void {
    setupTaskpaneActionHandlers({
        onOrgCodeInput: clearOrgCodeFieldValidation,
        onConnectClick: () => officeAuthFlow.handleConnectButtonClick(),
        onSwitchOrganisationCancelClick: () => officeAuthFlow.handleSwitchOrganisationCancel(),
        onFetchReport: () => {
            void reportDataCard.fetchReport({ intent: 'replace' });
        },
        onRefreshReportData: () => {
            void reportDataCard.fetchReport({ intent: 'refresh' });
        },
        onReportTargetChange: () => reportDataCard.handleReportingTargetChange(),
        onFilterRecordTypeChange: () => {
            clearReportFilterFieldHighlights();
            reportDataCard.resetReportingUi({ clearSummary: true });
            reportDataCard.syncRefreshButtonState();
        },
        onFiltersDomActivity: () => reportDataCard.syncRefreshButtonState(),
        onReportFiltersPanelDomActivity: () => {
            clearReportFilterFieldHighlights();
            reportDataCard.syncRefreshButtonState();
        },
    });
}

function syncTaskPaneChromeFromAuth(): void {
    syncTaskPaneChromeWithAuthState({
        tokenStore: traceTokenStore,
        session: taskPaneUiSession,
        userInfoStore: traceUserInfoStore,
        getPersistedOrgCodeForTitle: getOperatingOrgCodeForTitle,
        setOrgCodeInputDisabled,
        clearOrgCodeFieldValidation,
        logAuthDebug,
        reporting: reportDataCard,
        officeAuth: officeAuthFlow,
        labels: {
            switchOrganisation: SWITCH_ORGANISATION_BUTTON_LABEL,
            continueSwitchOrganisation: CONTINUE_SWITCH_ORGANISATION_BUTTON_LABEL,
            connectSignIn: CONNECT_BUTTON_LABEL_SIGN_IN,
        },
    });
}
